// Модель башни: стопка блоков + упругое качание верхних этажей.
// Качание — один гармонический осциллятор (угол theta): нижние этажи жёсткие,
// верхние flexibleFloors отклоняются по степенному профилю. Импульс приходит
// от неточной посадки, перфект гасит колебания.
import * as THREE from 'three';
import { disposeMesh } from './block.mjs';

const SLAB_TOP = 0.5; // верх бетонной плиты

export function createTower(scene, physics) {
  const sway = physics.sway;
  const group = new THREE.Group();
  scene.add(group);

  /** @type {{mesh:THREE.Mesh, baseX:number, width:number, height:number}[]} */
  let blocks = [];
  let theta = 0;     // текущий угол качания
  let thetaVel = 0;  // угловая скорость
  let lit = 0;       // сколько нижних этажей уже «заселено» (финальная волна)

  // Тёплый свет в окнах: поднимаем emissive фасада — на low-poly читается
  // именно как зажёгшийся в доме свет
  function setGlow(b, k) {
    const mats = Array.isArray(b.mesh.material) ? b.mesh.material : [b.mesh.material];
    for (const m of mats) {
      if (m.emissive) m.emissive.setRGB(k * 1.6, k * 1.15, k * 0.5);
    }
  }

  // Насколько блок с индексом i (снизу) отклоняется при данном theta
  function swayOffsetAt(i) {
    const n = blocks.length;
    const flex = sway.flexibleFloors;
    const start = Math.max(0, n - flex);
    if (i < start) return 0;
    const rel = (i - start + 1) / flex;
    return theta * Math.pow(rel, 1.6) * 3.2;
  }

  return {
    get count() { return blocks.length; },

    // Верх башни с учётом качания — сюда целится падающий блок
    topInfo(defaultWidth) {
      if (blocks.length === 0) {
        return { x: 0, y: SLAB_TOP, width: defaultWidth };
      }
      const i = blocks.length - 1;
      const b = blocks[i];
      return {
        x: b.baseX + swayOffsetAt(i),
        y: SLAB_TOP + blocks.length * b.height,
        width: b.width,
      };
    },

    // Высоту берём из конфига уровня: геометрия кэшируется и не хранит parameters
    addBlock(mesh, baseX, width, height) {
      blocks.push({ mesh, baseX, width, height });
      group.add(mesh);
    },

    // Сдвиг верхнего блока по сценарию уровня (пиксельные вырезы MahaNakhon)
    shiftTop(dx) {
      if (blocks.length === 0) return;
      const top = blocks[blocks.length - 1];
      top.baseX += dx;
      top.mesh.position.x = top.baseX;
    },

    /**
     * Волна заселения: этажи ниже fraction зажигают окна.
     * Меняем только пересечённую границу — не трогаем материалы каждый кадр.
     */
    lightUp(fraction) {
      const target = Math.round(blocks.length * Math.min(1, Math.max(0, fraction)));
      while (lit < target) {
        setGlow(blocks[lit], 0.13);   // тёплый свет, но фактура фасада видна
        lit++;
      }
    },

    // Погасить всё (рестарт уровня)
    lightsOff() {
      for (const b of blocks) setGlow(b, 0);
      lit = 0;
    },

    // Импульс от неточной посадки: чем больше промах и выше башня, тем сильнее
    impulse(offset) {
      const heightFactor = 0.5 + Math.min(1.5, blocks.length / 12);
      thetaVel += offset * sway.impulsePerOffset * heightFactor;
    },

    // Перфект стабилизирует башню
    calm() {
      thetaVel *= sway.perfectCalm;
      theta *= 0.8;
    },

    update(dt) {
      // Пружина с демпфером; угол ограничен, чтобы башня не «ложилась»
      const acc = -sway.omega * sway.omega * theta - sway.damping * thetaVel;
      thetaVel += acc * dt;
      theta += thetaVel * dt;
      theta = THREE.MathUtils.clamp(theta, -sway.maxTheta, sway.maxTheta);

      for (let i = 0; i < blocks.length; i++) {
        const b = blocks[i];
        const off = swayOffsetAt(i);
        b.mesh.position.x = b.baseX + off;
        b.mesh.rotation.z = off * 0.06;
      }
    },

    // Мгновенный сброс под рестарт уровня — с полной утилизацией текстур
    reset() {
      for (const b of blocks) {
        group.remove(b.mesh);
        disposeMesh(b.mesh);
      }
      blocks = [];
      theta = 0;
      thetaVel = 0;
      lit = 0;
    },

    SLAB_TOP,
  };
}
